/* global SideDrawer */
const TFX_SIDE_STRIP_IGNORED_PRODUCTS_KEY = 'tfx-cart-side-strip-ignored-products-v2';
const TFX_SIDE_STRIP_IGNORED_PRODUCTS_LEGACY_KEY = 'tfx-cart-side-strip-ignored-products';
const TFX_MOBILE_RECO_IGNORED_PRODUCTS_KEY = 'tfx-cart-mobile-reco-ignored-products-v1';
const TFX_MOBILE_CROSS_SELL_UNLOCKED_KEY = 'tfx-cart-mobile-cross-sell-unlocked-v1';

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
  }

  open(opener, elementToFocus, callback) {
    if (this.hasAttribute('open')) {
      this.opener = opener || this.opener;
      if (typeof callback === 'function') callback();
      return;
    }

    super.open(opener, elementToFocus, callback);
  }

  bindEvents() {
    if (this.__tfxEventsBound) return;
    this.__tfxEventsBound = true;

    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.handleDrawerCloseViaEvent.bind(this);
    /* BEGIN TFX_FIX: stable re-init of cart drawer featured carousel after merge/open */
    this.cartRefreshViaEventHandler = this.cartRefreshHandler.bind(this);
    this.beforeOpenHandler = this.handleBeforeOpen.bind(this);
    this.afterMergeHandler = this.handleAfterMerge.bind(this);
    this.afterOpenHandler = this.refreshUpsellSliders.bind(this);
    this.sideStripClickHandler = this.handleSideStripClick.bind(this);
    this.captureActionClickHandler = this.captureScrollBeforeAction.bind(this);
    this.captureActionChangeHandler = this.captureScrollBeforeAction.bind(this);
    this.captureActionSubmitHandler = this.captureScrollBeforeAction.bind(this);
    this.checkoutClickHandler = this.handleCheckoutClickDuringRefresh.bind(this);
    this.sideStripSubmitHandler = this.handleSideStripFormSubmit.bind(this);
    this.viewportChangeHandler = this.handleViewportChange.bind(this);
    this.pageShowHandler = this.handlePageShow.bind(this); // TFX_FIX
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshViaEventHandler);
    document.addEventListener('on:breakpoint-change', this.viewportChangeHandler);
    window.addEventListener('resize', this.viewportChangeHandler);
    window.addEventListener('pageshow', this.pageShowHandler); // TFX_FIX
    this.addEventListener('on:cart-drawer:before-open', this.beforeOpenHandler);
    this.addEventListener('on:cart:after-merge', this.afterMergeHandler);
    this.addEventListener('on:cart-drawer:after-open', this.afterOpenHandler);
    this.addEventListener('click', this.sideStripClickHandler);
    this.addEventListener('click', this.captureActionClickHandler, true);
    this.addEventListener('click', this.checkoutClickHandler, true);
    this.addEventListener('change', this.captureActionChangeHandler, true);
    this.addEventListener('submit', this.captureActionSubmitHandler, true);
    this.addEventListener('submit', this.sideStripSubmitHandler, true);
    this.clearLegacyIgnoredSideStripProducts();
    this.applySideStripIgnores();
    this.applyMobileRecoIgnores();
    /* END TFX_FIX: stable re-init of cart drawer featured carousel after merge/open */
  }

  disconnectedCallback() {
    this.cancelCheckoutWait();
    if (!this.__tfxEventsBound) return;

    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.removeEventListener('on:breakpoint-change', this.viewportChangeHandler);
    window.removeEventListener('resize', this.viewportChangeHandler);
    window.removeEventListener('pageshow', this.pageShowHandler); // TFX_FIX
    this.removeEventListener('on:cart-drawer:before-open', this.beforeOpenHandler);
    this.removeEventListener('on:cart:after-merge', this.afterMergeHandler);
    this.removeEventListener('on:cart-drawer:after-open', this.afterOpenHandler);
    this.removeEventListener('click', this.sideStripClickHandler);
    this.removeEventListener('click', this.captureActionClickHandler, true);
    this.removeEventListener('click', this.checkoutClickHandler, true);
    this.removeEventListener('change', this.captureActionChangeHandler, true);
    this.removeEventListener('submit', this.captureActionSubmitHandler, true);
    this.removeEventListener('submit', this.sideStripSubmitHandler, true);
    this.__tfxEventsBound = false;
  }

  handleBeforeOpen() {
    this.applySideStripIgnores();
    this.applyMobileRecoIgnores();
    this.applyMobileCrossSellGate();
    theme.manuallyLoadImages(this);
    this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
  }

  cancelCheckoutWait() {
    this.__tfxCheckoutEpoch = (this.__tfxCheckoutEpoch || 0) + 1;
    this.__tfxCheckoutRedirectPending = false;
    this.setCheckoutLoading(false); // TFX_FIX
  }

  /* BEGIN TFX_FIX: visible loading state on COMMANDER while waiting for cart sync.
     Always resolved by selector: the checkout button node is destroyed and rebuilt
     by theme.mergeNodes on every cart merge, so a captured reference goes stale. */
  setCheckoutLoading(isLoading) {
    this.querySelectorAll('.checkout-buttons .btn.is-loading').forEach((el) => {
      el.classList.remove('is-loading');
      el.removeAttribute('aria-disabled');
    });
    if (!isLoading) return;
    const checkoutLink = this.querySelector('.checkout-buttons a[href*="/checkout"]');
    if (!checkoutLink) return;
    checkoutLink.classList.add('is-loading');
    checkoutLink.setAttribute('aria-disabled', 'true');
  }

  handlePageShow() {
    // A bfcache restore must never bring the drawer back with a stuck loading state.
    this.cancelCheckoutWait();
  }
  /* END TFX_FIX */

  close(callback) {
    this.cancelCheckoutWait();
    super.close(callback);
  }

  handleAfterMerge() {
    this.restoreCapturedScroll();
    this.setCheckoutLoading(this.__tfxCheckoutRedirectPending === true); // TFX_FIX
    this.applySideStripIgnores();
    this.applyMobileRecoIgnores();
    if (this.__tfxPendingMobileCrossSellUnlock === true) {
      this.setMobileCrossSellUnlocked(true);
      this.__tfxPendingMobileCrossSellUnlock = false;
    }
    this.applyMobileCrossSellGate();
    theme.manuallyLoadImages(this);
    this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    this.refreshUpsellSliders();
  }

  /* BEGIN TFX_FIX: refresh upsell carousel once layout is painted to avoid stale/inactive nav buttons */
  refreshUpsellSliders() {
    requestAnimationFrame(() => {
      this.querySelectorAll('.cart-item-upsells carousel-slider').forEach((el) => {
        if (typeof el.refresh === 'function') {
          el.refresh();
        }
      });
    });
  }
  /* END TFX_FIX: refresh upsell carousel once layout is painted to avoid stale/inactive nav buttons */

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  handleDrawerCloseViaEvent(evt) {
    const forceClose = evt && evt.detail && evt.detail.force === true;
    const inDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
    if (!forceClose && !inDesignMode && this.__tfxCartMutationInFlight) return;
    this.close(null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    const cartForm = this.querySelector('cart-form');
    if (cartForm && typeof cartForm.refresh === 'function') {
      cartForm.refresh();
    }
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    const cartForm = this.querySelector('cart-form');
    if (cartForm && typeof cartForm.refresh === 'function') {
      this.captureDrawerScroll();
      // Bundled add responses can arrive out of order; read the current cart instead.
      cartForm.refresh();
    }
  }

  captureDrawerScroll() {
    if (!this.hasAttribute('open')) return;

    const content = this.querySelector('.cart-drawer__content');
    const sideStrip = this.querySelector('.tfx-cart-side-strip');
    this.__tfxCartMutationInFlight = true;
    clearTimeout(this.__tfxCartMutationSafetyTimeout);
    this.__tfxCartMutationSafetyTimeout = setTimeout(() => {
      this.__tfxCartMutationInFlight = false;
    }, 6000);
    this.__tfxPendingScroll = {
      contentScrollTop: content ? content.scrollTop : 0,
      sideStripScrollTop: sideStrip ? sideStrip.scrollTop : 0
    };
  }

  restoreCapturedScroll() {
    const pending = this.__tfxPendingScroll;
    if (!pending) return;
    this.__tfxPendingScroll = null;

    requestAnimationFrame(() => {
      const content = this.querySelector('.cart-drawer__content');
      if (content && typeof pending.contentScrollTop === 'number') {
        content.scrollTop = pending.contentScrollTop;
      }

      const sideStrip = this.querySelector('.tfx-cart-side-strip');
      if (sideStrip && typeof pending.sideStripScrollTop === 'number') {
        sideStrip.scrollTop = pending.sideStripScrollTop;
      }
    });
    this.__tfxCartMutationInFlight = false;
    clearTimeout(this.__tfxCartMutationSafetyTimeout);
  }

  handleViewportChange() {
    clearTimeout(this.__tfxViewportChangeTimeout);
    this.__tfxViewportChangeTimeout = setTimeout(() => {
      this.applySideStripIgnores();
      this.applyMobileRecoIgnores();
      this.applyMobileCrossSellGate();
    }, 120);
  }

  captureScrollBeforeAction(event) {
    if (!this.hasAttribute('open')) return;

    if (event.type === 'click') {
      const trigger = event.target.closest('.quantity-up, .quantity-down, .cart-item__remove, [data-tfx-side-strip-ignore], [data-tfx-mobile-reco-ignore], [name="add"]');
      if (!trigger || !this.contains(trigger)) return;
      if (trigger.closest('.tfx-cart-mobile-reco')) {
        this.__tfxPendingMobileCrossSellUnlock = true;
        this.unlockMobileCrossSells();
      } else if (
        trigger.matches('.quantity-up, .quantity-down, .cart-item__remove, [name="add"]')
        || trigger.hasAttribute('data-tfx-side-strip-ignore')
      ) {
        this.resetMobileCrossSells();
      }
      /* BEGIN TFX_FIX: n'armer le verrou de mutation que pour les actions qui touchent
         reellement au panier. Les boutons "J'ai deja a la maison" et un "+" desactive
         n'emettent aucune requete : sans requete il n'y a pas de merge, donc pas de
         restoreCapturedScroll(), et le verrou restait actif 6 s en gelant le bouton
         COMMANDER. La logique d'affichage ci-dessus reste inchangee. */
      const tfxIsIgnoreTrigger = trigger.hasAttribute('data-tfx-side-strip-ignore')
        || trigger.hasAttribute('data-tfx-mobile-reco-ignore');
      const tfxIsBlockedQuantityUp = trigger.matches('.quantity-up.unusable');
      if (tfxIsIgnoreTrigger || tfxIsBlockedQuantityUp) return;
      /* END TFX_FIX */
      this.captureDrawerScroll();
      return;
    }

    if (event.type === 'change') {
      const quantityInput = event.target.closest('.cart-item__quantity-input');
      if (!quantityInput || !this.contains(quantityInput)) return;
      this.resetMobileCrossSells();
      this.captureDrawerScroll();
      return;
    }

    if (event.type === 'submit') {
      const form = event.target.closest('.js-product-form');
      if (!form || !this.contains(form)) return;
      if (form.closest('.tfx-cart-mobile-reco')) {
        this.__tfxPendingMobileCrossSellUnlock = true;
        this.unlockMobileCrossSells();
      } else {
        this.resetMobileCrossSells();
      }
      this.captureDrawerScroll();
    }
  }

  getIgnoredSideStripProducts() {
    let rawValue = '[]';
    try {
      rawValue = window.sessionStorage.getItem(TFX_SIDE_STRIP_IGNORED_PRODUCTS_KEY) || '[]';
    } catch (error) {
      rawValue = this.__tfxIgnoredSideStripProductsRaw || '[]';
    }

    try {
      const parsed = JSON.parse(rawValue);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((value) => String(value)).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  clearLegacyIgnoredSideStripProducts() {
    if (this.__tfxLegacyIgnoredProductsCleared) return;
    this.__tfxLegacyIgnoredProductsCleared = true;

    try {
      window.sessionStorage.removeItem(TFX_SIDE_STRIP_IGNORED_PRODUCTS_LEGACY_KEY);
    } catch (error) {
      // No-op: sessionStorage can be blocked by browser privacy mode.
    }
  }

  setIgnoredSideStripProducts(productIds) {
    const normalized = Array.from(new Set((productIds || []).map((value) => String(value)).filter(Boolean)));
    const serialized = JSON.stringify(normalized);
    this.__tfxIgnoredSideStripProductsRaw = serialized;

    try {
      window.sessionStorage.setItem(TFX_SIDE_STRIP_IGNORED_PRODUCTS_KEY, serialized);
    } catch (error) {
      // No-op: sessionStorage can be blocked by browser privacy mode.
    }
  }

  addIgnoredSideStripProduct(productId) {
    const current = this.getIgnoredSideStripProducts();
    current.push(String(productId));
    this.setIgnoredSideStripProducts(current);
  }

  getIgnoredMobileRecoProducts() {
    let rawValue = '[]';
    try {
      rawValue = window.sessionStorage.getItem(TFX_MOBILE_RECO_IGNORED_PRODUCTS_KEY) || '[]';
    } catch (error) {
      rawValue = this.__tfxIgnoredMobileRecoProductsRaw || '[]';
    }

    try {
      const parsed = JSON.parse(rawValue);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((value) => String(value)).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  setIgnoredMobileRecoProducts(productIds) {
    const normalized = Array.from(new Set((productIds || []).map((value) => String(value)).filter(Boolean)));
    const serialized = JSON.stringify(normalized);
    this.__tfxIgnoredMobileRecoProductsRaw = serialized;

    try {
      window.sessionStorage.setItem(TFX_MOBILE_RECO_IGNORED_PRODUCTS_KEY, serialized);
    } catch (error) {
      // No-op: sessionStorage can be blocked by browser privacy mode.
    }
  }

  addIgnoredMobileRecoProduct(productId) {
    const current = this.getIgnoredMobileRecoProducts();
    current.push(String(productId));
    this.setIgnoredMobileRecoProducts(current);
  }

  getMobileCrossSellUnlockToken() {
    const crossSellMerge = this.querySelector('[data-merge="cross-sells"]');
    if (!crossSellMerge) return '';

    return String(crossSellMerge.dataset.mergeCache || '').trim();
  }

  isMobileCrossSellUnlocked() {
    const currentToken = this.getMobileCrossSellUnlockToken();
    if (!currentToken) return false;

    try {
      return window.sessionStorage.getItem(TFX_MOBILE_CROSS_SELL_UNLOCKED_KEY) === currentToken;
    } catch (error) {
      return this.__tfxMobileCrossSellUnlockedToken === currentToken;
    }
  }

  setMobileCrossSellUnlocked(unlocked) {
    const normalized = unlocked ? this.getMobileCrossSellUnlockToken() : '';
    this.__tfxMobileCrossSellUnlockedToken = normalized;
    try {
      window.sessionStorage.setItem(TFX_MOBILE_CROSS_SELL_UNLOCKED_KEY, normalized);
    } catch (error) {
      // No-op: sessionStorage can be blocked by browser privacy mode.
    }
  }

  unlockMobileCrossSells() {
    this.setMobileCrossSellUnlocked(true);
    this.applyMobileCrossSellGate();
    /* BEGIN TFX_FIX: re-mesurer le carrousel au deverrouillage.
       Tant que le verrou est pose, .cart-item-upsells est en display:none, donc
       carousel-slider s'initialise sur un element sans layout : gridWidth et
       slideSpan valent 0, slidesPerPage devient NaN et setCarouselState(false)
       pose [inactive], ce qui masque les fleches de navigation.
       Ce chemin de deverrouillage n'emet aucune requete panier : pas de merge,
       donc handleAfterMerge et son refreshUpsellSliders() ne s'executent jamais.
       refreshUpsellSliders() passe par requestAnimationFrame, la mesure a donc
       lieu apres que applyMobileCrossSellGate() a retire la classe de verrou. */
    this.refreshUpsellSliders();
    /* END TFX_FIX */
  }

  resetMobileCrossSells() {
    this.__tfxPendingMobileCrossSellUnlock = false;
    this.setMobileCrossSellUnlocked(false);
  }

  applyMobileCrossSellGate() {
    const crossSellMerge = this.querySelector('[data-merge="cross-sells"]');
    if (!crossSellMerge) return;

    crossSellMerge.classList.remove('tfx-mobile-cross-sells--locked');

    const isMobile = window.matchMedia('(max-width: 767.98px)').matches;
    if (!isMobile) return;

    const mobileRecoState = String(crossSellMerge.dataset.tfxMobileRecoState || 'none').trim();
    if (mobileRecoState !== 'suggested') return;

    const hasVisibleMobileReco = Array.from(this.querySelectorAll('.tfx-cart-mobile-reco')).some(
      (block) => !block.hidden && !block.closest('.merge-remove-wrapper, .merge-remove-item')
    );
    if (!hasVisibleMobileReco) return;

    if (!this.isMobileCrossSellUnlocked()) {
      crossSellMerge.classList.add('tfx-mobile-cross-sells--locked');
    }
  }

  applyMobileRecoIgnores() {
    const ignoredProductIds = new Set(this.getIgnoredMobileRecoProducts());
    this.querySelectorAll('.tfx-cart-mobile-reco').forEach((mobileReco) => {
      const card = mobileReco.querySelector('.tfx-upsell-card[data-product-id]');
      if (!card) {
        mobileReco.hidden = true;
        return;
      }

      const productId = String(card.dataset.productId || '');
      mobileReco.hidden = Boolean(productId && ignoredProductIds.has(productId));
    });
  }

  hideMobileRecoByProductId(productId) {
    if (!productId) return;
    const normalized = String(productId);
    this.querySelectorAll('.tfx-cart-mobile-reco').forEach((mobileReco) => {
      const card = mobileReco.querySelector('.tfx-upsell-card[data-product-id]');
      if (!card) return;
      if (String(card.dataset.productId || '') === normalized) {
        mobileReco.hidden = true;
      }
    });
  }

  applySideStripIgnores() {
    const cartForm = this.querySelector('cart-form');
    if (!cartForm) {
      this.classList.remove('tfx-side-strip-active');
      return;
    }

    const isDesktopOrTablet = window.matchMedia('(min-width: 768px)').matches;
    if (!isDesktopOrTablet) {
      cartForm.classList.add('tfx-cart-side-strip--session-hidden');
      cartForm.classList.remove('tfx-cart-drawer--with-side-strip');
      this.classList.remove('tfx-side-strip-active');
      return;
    }

    const sideStrip = cartForm.querySelector('.tfx-cart-side-strip-wrap .tfx-cart-side-strip');
    if (!sideStrip) {
      cartForm.classList.add('tfx-cart-side-strip--session-hidden');
      cartForm.classList.remove('tfx-cart-drawer--with-side-strip');
      this.classList.remove('tfx-side-strip-active');
      return;
    }

    const ignoredProductIds = new Set(this.getIgnoredSideStripProducts());
    const sideStripCards = sideStrip.querySelectorAll('.tfx-upsell-card--side-strip[data-product-id]');
    let visibleCount = 0;

    sideStripCards.forEach((card) => {
      const productId = String(card.dataset.productId || '');
      const sideStripItem = card.closest('.tfx-cart-side-strip__item');
      if (!sideStripItem) return;

      const isIgnored = productId && ignoredProductIds.has(productId);
      sideStripItem.hidden = isIgnored;
      if (!isIgnored) visibleCount += 1;
    });

    const hasVisibleItems = visibleCount > 0;
    cartForm.classList.toggle('tfx-cart-side-strip--session-hidden', !hasVisibleItems);
    cartForm.classList.toggle('tfx-cart-drawer--with-side-strip', hasVisibleItems);
    this.classList.toggle('tfx-side-strip-active', hasVisibleItems);
  }

  isCartRefreshPending() {
    const cartForm = this.querySelector('cart-form');
    return Boolean(
      theme.cartRequestsPending
      || this.__tfxCartMutationInFlight
      || (cartForm && cartForm.__tfxQuantityUpdateScheduled)
      || (cartForm && cartForm.classList.contains('cart-form--refreshing'))
    );
  }

  waitForCartRefresh(epoch = this.__tfxCheckoutEpoch || 0) {
    const startedAt = Date.now();
    const maxWaitMs = 6500;

    return new Promise((resolve) => {
      const check = () => {
        if (epoch !== (this.__tfxCheckoutEpoch || 0) || !this.isConnected || !this.hasAttribute('open')) {
          resolve(false);
          return;
        }
        if (!this.isCartRefreshPending()) {
          resolve(true);
          return;
        }
        if (Date.now() - startedAt >= maxWaitMs) {
          resolve(false);
          return;
        }

        window.setTimeout(check, 50);
      };

      check();
    });
  }

  handleCheckoutClickDuringRefresh(event) {
    const checkoutLink = event.target.closest('a[href*="/checkout"]');
    if (!checkoutLink || !this.contains(checkoutLink)) return;
    if (!this.isCartRefreshPending()) return;

    event.preventDefault();

    if (this.__tfxCheckoutRedirectPending) return;
    this.__tfxCheckoutRedirectPending = true;
    this.setCheckoutLoading(true); // TFX_FIX

    const checkoutUrl = checkoutLink.href;
    const epoch = this.__tfxCheckoutEpoch || 0;
    window.setTimeout(() => {
      if (epoch !== (this.__tfxCheckoutEpoch || 0) || !this.isConnected || !this.hasAttribute('open')) {
        this.setCheckoutLoading(false); // TFX_FIX
        return;
      }
      const termsCheckbox = this.querySelector('.terms-agreement input[type="checkbox"]');
      if (termsCheckbox && !termsCheckbox.checked) {
        this.__tfxCheckoutRedirectPending = false;
        this.setCheckoutLoading(false); // TFX_FIX
        return;
      }

      this.waitForCartRefresh(epoch).then((ready) => {
        if (epoch !== (this.__tfxCheckoutEpoch || 0) || !this.isConnected || !this.hasAttribute('open')) {
          this.setCheckoutLoading(false); // TFX_FIX
          return;
        }
        this.__tfxCheckoutRedirectPending = false;
        if (!ready || this.isCartRefreshPending()) {
          this.setCheckoutLoading(false); // TFX_FIX
          theme.showQuickPopup(
            document.documentElement.lang.startsWith('fr')
              ? 'Le panier est encore en cours de mise à jour. Veuillez réessayer dans quelques instants.'
              : 'Your cart is still updating. Please try again in a moment.',
            checkoutLink
          );
          return;
        }
        const currentTerms = this.querySelector('.terms-agreement input[type="checkbox"]');
        if (currentTerms && !currentTerms.checked) {
          this.setCheckoutLoading(false); // TFX_FIX
          return;
        }
        // Le spinner reste actif volontairement : la page part vers le checkout.
        window.location.href = checkoutUrl;
      });
    }, 0);
  }

  handleSideStripClick(event) {
    const ignoreBtn = event.target.closest('[data-tfx-side-strip-ignore]');
    if (ignoreBtn && this.contains(ignoreBtn)) {
      const card = ignoreBtn.closest('.tfx-upsell-card--side-strip[data-product-id]');
      if (!card) return;

      const productId = String(card.dataset.productId || '');
      if (!productId) return;

      event.preventDefault();
      this.addIgnoredSideStripProduct(productId);
      this.applySideStripIgnores();
      return;
    }

    const mobileIgnoreBtn = event.target.closest('[data-tfx-mobile-reco-ignore]');
    if (!mobileIgnoreBtn || !this.contains(mobileIgnoreBtn)) return;

    const mobileReco = mobileIgnoreBtn.closest('.tfx-cart-mobile-reco');
    if (!mobileReco) return;

    const mobileRecoCard = mobileReco.querySelector('.tfx-upsell-card[data-product-id]');
    if (!mobileRecoCard) return;

    const mobileRecoProductId = String(mobileRecoCard.dataset.productId || '');
    if (!mobileRecoProductId) return;

    event.preventDefault();
    this.unlockMobileCrossSells();
    this.addIgnoredMobileRecoProduct(mobileRecoProductId);
    this.applyMobileRecoIgnores();
    this.applyMobileCrossSellGate();
  }

  async handleSideStripFormSubmit(event) {
    const form = event.target.closest('.tfx-cart-side-strip .js-product-form, .tfx-cart-mobile-reco .js-product-form');
    if (!form || !this.contains(form)) return;

    form.dataset.tfxSideStripHandled = '1';
    event.preventDefault();

    if (form.dataset.tfxSubmitting === '1') return;
    form.dataset.tfxSubmitting = '1';

    const addBtn = form.querySelector('[name="add"]');

    if (addBtn) {
      addBtn.setAttribute('aria-disabled', 'true');
      addBtn.classList.add('is-loading');
    }

    try {
      this.captureDrawerScroll();

      const formData = new FormData(form);
      const response = await theme.cartFetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        const message = (data && (data.description || data.message)) || `HTTP error! Status: ${response.status}`;
        throw new Error(message);
      }

      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: {
          variantId: data.variant_id
        }
      }));

      const mobileRecoCard = form.closest('.tfx-cart-mobile-reco')?.querySelector('.tfx-upsell-card[data-product-id]');
      if (mobileRecoCard) {
        this.hideMobileRecoByProductId(mobileRecoCard.dataset.productId);
        this.applyMobileCrossSellGate();
      }

      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
    } catch (error) {
      form.closest('product-form')?.setErrorMsgState(error.message || 'Add to cart failed');
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: {
          error: error && error.message ? error.message : 'Add to cart failed'
        }
      }));
      this.__tfxCartMutationInFlight = false;
      clearTimeout(this.__tfxCartMutationSafetyTimeout);
    } finally {
      form.dataset.tfxSubmitting = '0';
      form.dataset.tfxSideStripHandled = '0';
      if (addBtn) {
        addBtn.classList.remove('is-loading');
        addBtn.classList.remove('is-success');
        addBtn.removeAttribute('aria-disabled');
      }
    }
  }
}

window.customElements.define('cart-drawer', CartDrawer);
